import { useEffect, useState, type FormEvent } from 'react';
import { CircleAlert, CircleCheck, Gavel, LogOut, ShieldAlert, ShieldCheck, TriangleAlert } from 'lucide-react';
import { MODERATION, type AppealSummary, type StandingAction, type StandingLevel } from '@spawnwire/shared';
import { LogoMark } from '../../components/Logo';
import { Button, clsx, Modal, Spinner, TextArea } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { onReady } from '../../lib/gateway';
import { actionTitle, formatDate, LEVEL_INFO, LEVELS, RESTRICTION_HINT, RESTRICTION_LABEL, RULE_LABEL, statusLine, timeLeft } from '../../lib/moderation';
import { useNow } from '../../lib/hooks';
import { ui } from '../../store/ui';
import { logout } from '../shell/UserPanel';
import { standing, useStanding } from './store';

const LEVEL_ICON: Record<StandingLevel, typeof ShieldCheck> = { good: ShieldCheck, limited: CircleAlert, at_risk: TriangleAlert, banned: Gavel };

function AppealStatus({ appeal }: { appeal: AppealSummary }) {
  return (
    <div className={clsx('mt-3 rounded-lg border p-3 text-sm', appeal.status === 'denied' ? 'border-danger/40' : appeal.status === 'accepted' ? 'border-success/40' : 'border-line')}>
      <p className="font-medium text-fg">
        {appeal.status === 'open' ? 'Your appeal is being reviewed' : appeal.status === 'accepted' ? 'Your appeal was accepted' : 'Your appeal was denied'}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-muted">“{appeal.message}”</p>
      {appeal.response && (
        <p className="mt-2 whitespace-pre-wrap text-fg">
          <span className="font-medium">Spawnwire staff:</span> {appeal.response}
        </p>
      )}
    </div>
  );
}

/** Asks staff to take another look at an action. One appeal per action. */
function AppealForm({ action, onDone }: { action: StandingAction; onDone?: () => void }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (action.appeal) return <AppealStatus appeal={action.appeal} />;
  if (!action.active) return null;
  if (!open) {
    return (
      <Button size="sm" variant="secondary" className="mt-3" onClick={() => setOpen(true)}>
        Appeal
      </Button>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const appeal = await http.post<AppealSummary>(`/users/@me/standing/${action.id}/appeal`, { message: message.trim() });
      standing.patch(action.id, { appeal });
      onDone?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-2">
      <TextArea
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={2000}
        placeholder="Explain why you think this was a mistake, or anything staff should know. You can only appeal once."
        aria-label="Appeal message"
        autoFocus
      />
      {error && <p className="text-sm text-danger">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={busy} disabled={message.trim().length < 10}>
          Send appeal
        </Button>
      </div>
    </form>
  );
}

function ActionCard({ action }: { action: StandingAction }) {
  return (
    <li className={clsx('rounded-xl border bg-sidebar p-4', action.active ? 'border-line-strong' : 'border-line opacity-80')}>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={clsx(
            'rounded-full px-2 py-0.5 text-xs font-semibold',
            !action.active ? 'bg-active text-muted' : action.type === 'ban' ? 'bg-danger text-white' : action.type === 'restriction' ? 'bg-[#f97316] text-white' : 'bg-warning text-black',
          )}
        >
          {actionTitle(action)}
        </span>
        <span className="text-sm font-medium text-fg">{RULE_LABEL[action.rule]}</span>
        <span className="ml-auto text-xs text-faint">{formatDate(action.createdAt)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-fg">{action.reason}</p>
      {action.type === 'restriction' && action.restriction && action.active && <p className="mt-1 text-xs text-muted">{RESTRICTION_HINT[action.restriction]}</p>}
      <p className="mt-2 text-xs text-muted">{statusLine(action)}</p>
      {action.revokedAt && action.revokeReason && <p className="mt-1 text-xs text-muted">Reason: {action.revokeReason}</p>}
      <AppealForm action={action} />
    </li>
  );
}

/** The meter from good to banned, with where the account is now. */
function LevelMeter({ level }: { level: StandingLevel }) {
  const at = LEVELS.indexOf(level);
  return (
    <div className="mt-5">
      <div className="grid grid-cols-4 gap-1.5">
        {LEVELS.map((l, i) => (
          <div key={l} className={clsx('h-2 rounded-full', i <= at ? LEVEL_INFO[level].bar : 'bg-active')} />
        ))}
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1.5 text-xs">
        {LEVELS.map((l) => (
          <span key={l} className={clsx(l === level ? `font-semibold ${LEVEL_INFO[l].tone}` : 'text-faint')}>
            {LEVEL_INFO[l].label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Settings → Account standing. */
export function AccountStandingSection() {
  const current = useStanding((s) => s.standing);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    standing.load().catch((err) => setError(errorMessage(err)));
  }, []);

  if (!current) return error ? <p className="text-sm text-danger">{error}</p> : <Spinner />;
  const info = LEVEL_INFO[current.level];
  const Icon = LEVEL_ICON[current.level];
  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-line bg-sidebar p-5">
        <div className="flex items-start gap-4">
          <span className={clsx('flex size-12 shrink-0 items-center justify-center rounded-full bg-main', info.tone)}>
            <Icon className="size-6" aria-hidden />
          </span>
          <div>
            <h2 className="text-lg font-bold text-fg">{info.title}</h2>
            <p className="mt-1 text-sm text-muted">{info.body}</p>
          </div>
        </div>
        <LevelMeter level={current.level} />
        {current.restrictions.length > 0 && (
          <ul className="mt-5 space-y-1 text-sm">
            {current.restrictions.map((r) => (
              <li key={r} className="flex items-start gap-2 text-fg">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-[#f97316]" aria-hidden />
                <span>
                  <span className="font-medium">{RESTRICTION_LABEL[r]} restricted.</span> <span className="text-muted">{RESTRICTION_HINT[r]}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">History</h2>
        <p className="mt-1 text-sm text-faint">
          Warnings count toward your standing for {MODERATION.warningActiveDays} days. Everything stays listed here so you always know where you stand. You can appeal anything that is still in force.
        </p>
        {current.actions.length === 0 ? (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-line bg-sidebar p-4 text-sm text-muted">
            <CircleCheck className="size-5 text-success" aria-hidden /> No warnings or actions on your account.
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {current.actions.map((a) => (
              <ActionCard key={a.id} action={a} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Shows a new warning or restriction once, until the person says they understand. */
export function StandingNotice() {
  const current = useStanding((s) => s.standing);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = () => void standing.load().catch(() => {});
    load();
    return onReady(load);
  }, []);

  const pending = current?.actions.find((a) => a.active && !a.acknowledged && a.type !== 'ban');
  if (!pending) return null;

  async function acknowledge() {
    setBusy(true);
    try {
      await http.post(`/users/@me/standing/${pending!.id}/acknowledge`);
      standing.patch(pending!.id, { acknowledged: true });
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const warning = pending.type === 'warning';
  return (
    <Modal
      open
      onClose={() => void acknowledge()}
      title={warning ? 'Your account received a warning' : `Your account has a restriction: ${RESTRICTION_LABEL[pending.restriction!]}`}
      description={`Rule: ${RULE_LABEL[pending.rule]}`}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              void acknowledge();
              ui.open({ kind: 'userSettings', section: 'standing' });
            }}
          >
            View account standing
          </Button>
          <Button loading={busy} onClick={() => void acknowledge()}>
            I understand
          </Button>
        </>
      }
    >
      <p className="whitespace-pre-wrap rounded-lg bg-main p-3 text-sm text-fg">{pending.reason}</p>
      {!warning && pending.restriction && <p className="mt-3 text-sm text-muted">{RESTRICTION_HINT[pending.restriction]}</p>}
      <p className="mt-3 text-sm text-muted">
        {warning
          ? `Warnings count toward your account standing for ${MODERATION.warningActiveDays} days. Several warnings can lead to your account being banned.`
          : pending.expiresAt
            ? `This ends ${formatDate(pending.expiresAt)}.`
            : 'This restriction is permanent.'}{' '}
        If you think this was a mistake, you can appeal it in Settings → Account standing.
      </p>
    </Modal>
  );
}

/** Replaces the whole app while the account is banned: why, until when, and a way to appeal. */
export function BanScreen({ ban }: { ban: StandingAction }) {
  const now = useNow(30_000);
  const over = ban.expiresAt !== null && Date.parse(ban.expiresAt) <= now;

  // Check now and then whether the ban was lifted (an accepted appeal, or staff changed their mind).
  useEffect(() => {
    const check = () => void standing.load().catch(() => {});
    const timer = setInterval(check, 60_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-main p-4">
      <div className="w-full max-w-lg rounded-2xl border border-line bg-sidebar p-6 shadow-pop sm:p-8">
        <div className="flex items-center gap-3">
          <LogoMark size={36} />
          <span className="flex size-10 items-center justify-center rounded-full bg-danger/15 text-danger">
            <Gavel className="size-5" aria-hidden />
          </span>
        </div>
        <h1 className="mt-5 text-2xl font-bold text-fg">{ban.expiresAt ? 'Your account is temporarily banned' : 'Your account is banned'}</h1>
        <p className="mt-2 text-sm text-muted">Spawnwire staff banned your account for breaking the rules. While it is banned you can't use Spawnwire or your game servers.</p>

        <dl className="mt-5 space-y-3 rounded-xl bg-main p-4 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-faint">Rule</dt>
            <dd className="mt-0.5 text-fg">{RULE_LABEL[ban.rule]}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-faint">Reason</dt>
            <dd className="mt-0.5 whitespace-pre-wrap text-fg">{ban.reason}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-faint">{ban.expiresAt ? 'Ends' : 'Length'}</dt>
            <dd className="mt-0.5 text-fg">{ban.expiresAt ? (over ? 'Now' : `${formatDate(ban.expiresAt)} (in ${timeLeft(ban.expiresAt, now)})`) : 'Permanent'}</dd>
          </div>
        </dl>

        {over ? (
          <Button className="mt-5 w-full" onClick={() => location.reload()}>
            Your ban is over. Continue to Spawnwire
          </Button>
        ) : (
          <>
            <h2 className="mt-6 text-sm font-semibold text-fg">Think this was a mistake?</h2>
            <AppealForm action={ban} />
          </>
        )}

        <Button variant="ghost" icon={LogOut} className="mt-6 w-full" onClick={() => void logout().then(() => standing.clear())}>
          Log out
        </Button>
      </div>
    </div>
  );
}
